import React from 'react';
import { X, AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react';

interface NotificationModalProps {
    isOpen: boolean;
    type: 'info' | 'success' | 'warning' | 'error';
    title: string;
    message: string;
    onClose: () => void;
    onConfirm?: () => void;
    confirmText?: string;
    cancelText?: string;
    showCancel?: boolean;
    showGoToSubscription?: boolean;
    onGoToSubscription?: () => void;
}

const NotificationModal: React.FC<NotificationModalProps> = ({
    isOpen,
    type,
    title,
    message,
    onClose,
    onConfirm,
    confirmText = 'OK',
    cancelText = 'Anuluj',
    showCancel = false,
    showGoToSubscription = false,
    onGoToSubscription
}) => {
    if (!isOpen) return null;

    const icons = {
        info: <Info size={24} className="text-blue-500" />,
        success: <CheckCircle size={24} className="text-green-500" />,
        warning: <AlertTriangle size={24} className="text-yellow-500" />,
        error: <AlertCircle size={24} className="text-red-500" />
    };

    const bgColors = {
        info: 'bg-blue-50 border-blue-200',
        success: 'bg-green-50 border-green-200',
        warning: 'bg-yellow-50 border-yellow-200',
        error: 'bg-red-50 border-red-200'
    };

    const buttonColors = {
        info: 'bg-blue-600 hover:bg-blue-700 text-white',
        success: 'bg-green-600 hover:bg-green-700 text-white',
        warning: 'bg-yellow-600 hover:bg-yellow-700 text-white',
        error: 'bg-red-600 hover:bg-red-700 text-white'
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
            <div className={`bg-white rounded-2xl shadow-2xl max-w-md w-full ${bgColors[type]} border-2 animate-in fade-in zoom-in duration-200`}>
                <div className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                        <div className="flex-shrink-0 mt-1">
                            {icons[type]}
                        </div>
                        <div className="flex-1">
                            <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
                            <p className="text-gray-700 leading-relaxed">{message}</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors p-1"
                        >
                            <X size={20} />
                        </button>
                    </div>
                    
                    <div className="flex justify-end gap-3 mt-6">
                        {showCancel && (
                            <button
                                onClick={onClose}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                            >
                                {cancelText}
                            </button>
                        )}
                        {showGoToSubscription && onGoToSubscription && (
                            <button
                                onClick={() => {
                                    onGoToSubscription();
                                    onClose();
                                }}
                                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                            >
                                Przejdź do subskrypcji
                            </button>
                        )}
                        <button
                            onClick={() => {
                                if (onConfirm) {
                                    onConfirm();
                                }
                                onClose();
                            }}
                            className={`px-6 py-2 rounded-lg transition-colors font-medium ${buttonColors[type]}`}
                        >
                            {confirmText}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NotificationModal;

